// ════════════════════════════════════════════════════════════════════
// src/context/AuthContext.js — quem sou eu E em qual conta eu trabalho
// ════════════════════════════════════════════════════════════════════
// Antes este arquivo respondia uma pergunta só: quem está logado. Com
// equipe, passam a ser duas, e a segunda é a que importa para os dados:
//
//   user.uid  = quem está sentado na cadeira (o vendedor, o suporte)
//   contaUid  = de quem são os dados (sempre o franqueado dono)
//
// Toda tela lê users/{contaUid}/... — nunca mais users/{user.uid}/...
// É isso que faz o vendedor enxergar a carteira do patrão em vez de uma
// conta vazia.
// ════════════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth, db } from '../services/firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { podeArea, PAPEL_PADRAO } from '../permissoes';

const AuthContext = createContext(null);

// E-mail é chave de documento: minúsculo e sem espaço, sempre.
export const chaveEmail = (e) => String(e || '').trim().toLowerCase();

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // conta ativa
  const [contaUid, setContaUid] = useState(null);
  const [papel, setPapel]       = useState('admin');
  const [vinculo, setVinculo]   = useState(null);   // null = sou dono da minha conta
  const [convitePendente, setConvitePendente] = useState(null);

  // ── Descobre em qual conta este login trabalha ────────────────────
  // Ordem: já tem vínculo? usa. Senão, é dono da própria conta — e, se
  // existir convite para o e-mail dele, guarda para PERGUNTAR.
  const resolverConta = useCallback(async (u) => {
    if (!u) {
      setContaUid(null); setPapel('admin'); setVinculo(null); setConvitePendente(null);
      return;
    }

    // 1. vínculo existente
    try {
      const vSnap = await getDoc(doc(db, 'vinculos', u.uid));
      if (vSnap.exists()) {
        const v = vSnap.data();
        if (v.ativo !== false && v.donoUid) {
          setContaUid(v.donoUid);
          setPapel(v.papel || PAPEL_PADRAO);
          setVinculo({ id: u.uid, ...v });
          setConvitePendente(null);
          return;
        }
        // suspenso pelo admin: cai para a própria conta (que estará vazia)
        setVinculo({ id: u.uid, ...v, suspenso: true });
      } else {
        setVinculo(null);
      }
    } catch (e) {
      // regra ainda não publicada ou rede: segue como dono da própria conta
      setVinculo(null);
    }

    setContaUid(u.uid);
    setPapel('admin');

    // 2. convite aberto para o meu e-mail?
    // NÃO aceitamos sozinho de propósito. Qualquer pessoa consegue criar um
    // convite apontando para um e-mail alheio; aceitar automático jogaria
    // alguém para dentro da conta de um estranho sem perceber. Perguntamos.
    const email = chaveEmail(u.email);
    if (!email) { setConvitePendente(null); return; }
    try {
      const cSnap = await getDoc(doc(db, 'convites', email));
      setConvitePendente(cSnap.exists() ? { id: email, ...cSnap.data() } : null);
    } catch (e) {
      setConvitePendente(null);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) localStorage.setItem('fl_uid', u.uid);
      else   localStorage.removeItem('fl_uid');
      await resolverConta(u);
      setLoading(false);
    });
    return unsub;
  }, [resolverConta]);

  // guarda a conta ativa para quem ainda lê do localStorage
  useEffect(() => {
    if (contaUid) localStorage.setItem('fl_conta', contaUid);
    else localStorage.removeItem('fl_conta');
  }, [contaUid]);

  // ── Aceitar / recusar convite ─────────────────────────────────────
  const aceitarConvite = async () => {
    if (!user || !convitePendente) return;
    await setDoc(doc(db, 'vinculos', user.uid), {
      donoUid:   convitePendente.donoUid,
      donoEmail: convitePendente.donoEmail || '',
      papel:     convitePendente.papel || PAPEL_PADRAO,
      email:     chaveEmail(user.email),
      nome:      user.displayName || convitePendente.nome || '',
      ativo:     true,
      criadoEm:  new Date().toISOString(),
    });
    // o convite cumpriu o papel dele; some para não reaparecer
    try { await deleteDoc(doc(db, 'convites', convitePendente.id)); } catch (e) {}
    await resolverConta(user);
  };

  const recusarConvite = async () => {
    if (!convitePendente) return;
    try { await deleteDoc(doc(db, 'convites', convitePendente.id)); } catch (e) {}
    setConvitePendente(null);
  };

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signup = async (nome, email, password) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: nome });
    return cred;
  };

  const logout = () => {
    localStorage.removeItem('fl_uid');
    localStorage.removeItem('fl_conta');
    return signOut(auth);
  };

  const valor = {
    user, loading,
    contaUid,
    papel,
    vinculo,
    ehDono: !!user && contaUid === user.uid,
    pode: (area) => podeArea(papel, area),
    convitePendente, aceitarConvite, recusarConvite,
    recarregarConta: () => resolverConta(user),
    login, signup, logout,
  };

  return (
    <AuthContext.Provider value={valor}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
